import type { HabitCategory, WeekOutcome } from '@contracts';
import { formatShortDate } from '@ui/shared/format/date';

const WIDTH = 64;
const HEIGHT = 20;

const CATEGORY_VIVID_VAR: Record<HabitCategory, string> = {
  health: 'var(--accent-health-vivid)',
  self_care: 'var(--accent-selfcare-vivid)',
  study: 'var(--accent-study-vivid)',
  other: 'var(--accent-other-vivid)',
};

/**
 * Hand-rolled inline SVG sparkline (§1.3.1, WR-2/AC-W5) — no charting
 * library. Up to 4 points from `HabitWeekReport.trend`, oldest to newest
 * left-to-right, height = that week's completion ratio, bottom-anchored.
 * Fewer than 4 weeks of history renders exactly that many points — never
 * padded with synthetic zero-weeks, never a fixed 4-slot chart with dashed
 * placeholders (the corrected v3.0 rendering).
 */
export function TrendSparkline({
  trend,
  category,
}: {
  readonly trend: readonly WeekOutcome[];
  readonly category: HabitCategory;
}) {
  if (trend.length === 0) {
    return (
      <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-disabled)' }} aria-label="No trend history yet">
        —
      </span>
    );
  }

  const ratios = trend.map((w) => (w.planned > 0 ? Math.min(1, w.actual / w.planned) : 0));
  const n = trend.length;
  const stepX = n > 1 ? WIDTH / (n - 1) : 0;
  const points = ratios.map((r, i) => {
    const x = n > 1 ? i * stepX : WIDTH / 2;
    const y = HEIGHT - 2 - r * (HEIGHT - 4);
    return { x, y };
  });

  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const last = points[points.length - 1]!;

  const ariaLabel = `${describeTrend(trend)}`;

  return (
    <svg width={WIDTH} height={HEIGHT} viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="img" aria-label={ariaLabel}>
      <path d={path} fill="none" stroke="var(--text-secondary)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last.x} cy={last.y} r={3} fill={CATEGORY_VIVID_VAR[category]}>
        <title>{`This week: ${trend[trend.length - 1]!.actual} of ${trend[trend.length - 1]!.planned}`}</title>
      </circle>
    </svg>
  );
}

function describeTrend(trend: readonly WeekOutcome[]): string {
  const parts = trend.map((w) => `Week of ${formatShortDate(w.weekStart)}: ${w.actual} of ${w.planned}`);
  return parts.join(', ');
}
